import React, { useState, useEffect } from 'react';
import './LeadCaptureForm.css';

const TierSelection = () => {
    const [formData, setFormData] = useState(null);
    const [selectedTier, setSelectedTier] = useState('Professional'); // Default recommendation
    const [paymentTerm, setPaymentTerm] = useState('Annual'); // Default to best value
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    
    // Beta customer flag (first 10 get 25% discount)
    const isBeta = true; // You could fetch this from backend if needed
    
    // Pricing data
    const pricing = {
        beta: {
            Essential: {
                Annual: 27000,
                Quarterly: 7763,
                Monthly: 2813
            },
            Professional: {
                Annual: 48750,
                Quarterly: 14016,
                Monthly: 5094
            },
            Enterprise: {
                Annual: 82500,
                Quarterly: 23719,
                Monthly: 8594
            }
        }
    };
    
    // Tier features
    const tiers = {
        Essential: {
            name: 'Essential',
            tagline: 'Building security foundations',
            features: [
                'Single cloud environment',
                'Up to 50 network nodes',
                '2 compliance frameworks',
                'Quarterly executive reports',
                '3 user seats',
                'Email support (24-48hr)'
            ]
        },
        Professional: {
            name: 'Professional',
            tagline: 'Comprehensive security program',
            features: [
                'Multi-cloud support (AWS/Azure/GCP)',
                'Up to 250 network nodes',
                'All major compliance frameworks',
                'Monthly executive reports',
                '10 user seats',
                'Priority support (12-24hr)',
                'Monthly strategy calls',
                'Advanced analytics & reporting'
            ],
            recommended: true
        },
        Enterprise: {
            name: 'Enterprise',
            tagline: 'White-glove security leadership',
            features: [
                'Unlimited cloud resources',
                'Unlimited network nodes',
                'All frameworks + custom',
                'Weekly executive reports',
                'Unlimited user seats',
                '24/7 support (4hr response)',
                'Weekly strategy calls',
                'Dedicated CSM',
                '10 consulting hours/month'
            ]
        }
    };
    
    // Load form data from sessionStorage
    useEffect(() => {
        const storedData = sessionStorage.getItem('leadFormData');
        if (storedData) {
            setFormData(JSON.parse(storedData));
        } else {
            // If no form data, redirect back to form
            window.location.href = '/lead-capture';
        }
    }, []);
    
    // Auto-recommend tier based on company size
    useEffect(() => {
        if (formData && formData.company_size) {
            const size = formData.company_size;
            if (size === '10M-50M' || size === '50M-100M') {
                setSelectedTier('Essential');
            } else if (size === '100M-250M' || size === '250M-500M') {
                setSelectedTier('Professional');
            } else {
                setSelectedTier('Enterprise');
            }
        }
    }, [formData]);
    
    const getPrice = (tier, term) => {
        const priceType = isBeta ? 'beta' : 'standard';
        return pricing[priceType][tier][term];
    };
    
    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(price);
    };
    
    const calculateSavings = (tier, term) => {
        const annualPrice = getPrice(tier, 'Annual');
        const termPrice = getPrice(tier, term);
        
        if (term === 'Annual') {
            return null;
        }
        
        const annualEquivalent = term === 'Quarterly' ? termPrice * 4 : termPrice * 12;
        const savings = annualEquivalent - annualPrice;
        const savingsPercent = Math.round((savings / annualEquivalent) * 100);
        
        return { amount: savings, percent: savingsPercent };
    };
    
    const handleSubmit = async () => {
        if (!selectedTier || !paymentTerm) {
            setError('Please select a tier and payment term');
            return;
        }
        
        setIsSubmitting(true);
        setError(null);
        
        try {
            // Combine form data with tier selection
            const submitData = {
                ...formData,
                tier_requested: selectedTier,
                payment_term: paymentTerm
            };
            
            // Submit to backend API
            const response = await fetch('/api/leads', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(submitData)
            });
            
            if (!response.ok) {
                throw new Error('Failed to submit lead');
            }
            
            const result = await response.json();
            
            // Clear session storage
            sessionStorage.removeItem('leadFormData');
            
            // Store lead ID for thank you page
            sessionStorage.setItem('leadId', result.id);
            
            // Redirect to thank you page
            window.location.href = '/thank-you';
            
        } catch (err) {
            console.error('Submission error:', err);
            setError('An error occurred while submitting your request. Please try again or contact us directly.');
            setIsSubmitting(false);
        }
    };
    
    if (!formData) {
        return (
            <div className="lead-capture-page">
                <div className="form-container">
                    <p>Loading...</p>
                </div>
            </div>
        );
    }
    
    return (
        <div className="lead-capture-page">
            <div className="form-container" style={{ maxWidth: '900px' }}>
                <h2>Select Your CISO Advisor Tier</h2>
                
                {isBeta && (
                    <div className="info">
                        <strong>🎉 Beta Customer Pricing</strong>
                        <br />
                        You're one of our first 10 customers! Enjoy 25% off, locked in for life.
                    </div>
                )}
                
                {error && <div className="error">{error}</div>}
                
                {/* Payment Term Selection */}
                <h3>Payment Terms</h3>
                <div className="payment-terms">
                    <div 
                        className={`payment-term-option best-value ${paymentTerm === 'Annual' ? 'selected' : ''}`}
                        onClick={() => setPaymentTerm('Annual')}
                    >
                        <div className="payment-term-name">Annual</div>
                        <div className="payment-term-price">Prepay & Save</div>
                    </div>
                    
                    <div 
                        className={`payment-term-option ${paymentTerm === 'Quarterly' ? 'selected' : ''}`}
                        onClick={() => setPaymentTerm('Quarterly')}
                    >
                        <div className="payment-term-name">Quarterly</div>
                        <div className="payment-term-price">Pay Every 3 Months</div>
                        <div className="payment-term-savings">15% premium</div>
                    </div>
                    
                    <div 
                        className={`payment-term-option ${paymentTerm === 'Monthly' ? 'selected' : ''}`}
                        onClick={() => setPaymentTerm('Monthly')}
                    >
                        <div className="payment-term-name">Monthly</div>
                        <div className="payment-term-price">Pay Monthly</div>
                        <div className="payment-term-savings">25% premium</div>
                    </div>
                </div>
                
                {/* Tier Cards */}
                <h3>Choose Your Tier</h3>
                <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                    Based on your company size ({formData.company_size}), we recommend <strong>{selectedTier}</strong>
                </p>
                
                <div className="tier-cards">
                    {Object.entries(tiers).map(([tierKey, tierData]) => {
                        const price = getPrice(tierKey, paymentTerm);
                        const savings = calculateSavings(tierKey, paymentTerm);
                        
                        return (
                            <div 
                                key={tierKey}
                                className={`tier-card ${selectedTier === tierKey ? 'selected' : ''} ${tierData.recommended ? 'recommended' : ''}`}
                                onClick={() => setSelectedTier(tierKey)}
                            >
                                <div className="tier-name">
                                    {tierData.name}
                                    {isBeta && <span className="beta-badge">BETA</span>}
                                </div>
                                <div className="tier-price">{formatPrice(price)}</div>
                                <div className="tier-price-term">
                                    {paymentTerm === 'Annual' ? 'per year' : 
                                     paymentTerm === 'Quarterly' ? 'per quarter' : 'per month'}
                                </div>
                                {savings && (
                                    <div style={{ fontSize: '0.85rem', color: '#e74c3c', marginBottom: '0.5rem' }}>
                                        Save {formatPrice(savings.amount)}/year with annual
                                    </div>
                                )}
                                <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                                    {tierData.tagline}
                                </div>
                                <ul className="tier-features">
                                    {tierData.features.map((feature, idx) => (
                                        <li key={idx}>{feature}</li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>
                
                {/* Summary */}
                <div style={{ background: '#f8f9fa', padding: '1.5rem', borderRadius: '10px', marginTop: '2rem' }}>
                    <h3 style={{ marginTop: 0 }}>Your Selection</h3>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span><strong>Company:</strong> {formData.company_name}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span><strong>Tier:</strong> {selectedTier}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span><strong>Payment:</strong> {paymentTerm}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.25rem', fontWeight: '600', color: '#3498db', marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid #ddd' }}>
                        <span>Total:</span>
                        <span>{formatPrice(getPrice(selectedTier, paymentTerm))}</span>
                    </div>
                </div>
                
                {/* Action buttons */}
                <div className="button-group" style={{ marginTop: '2rem' }}>
                    <button 
                        type="button" 
                        className="btn btn-secondary"
                        onClick={() => window.location.href = '/lead-capture'}
                        disabled={isSubmitting}
                    >
                        Back
                    </button>
                    <button 
                        type="button" 
                        className="btn btn-success"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="loading"></span>
                                Submitting...
                            </>
                        ) : (
                            'Request Quote'
                        )}
                    </button>
                </div>
                
                <p style={{ textAlign: 'center', marginTop: '1.5rem', color: '#666', fontSize: '0.9rem' }}>
                    No payment required now. We'll send you an invoice to review.
                </p>
            </div>
        </div>
    );
};

export default TierSelection;
